import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { SettingsTabBar } from '../../components/settings/SettingsTabBar';
import { SettingsTabItem } from '../../components/settings/SettingsTabItem';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { JobAttributionListItem } from '../../components/people/JobAttributionListItem';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  CollaboratorDependent,
  CollaboratorEducation,
  CollaboratorProfile,
  getCollaboratorProfile,
  upsertCollaboratorProfile,
} from '../../services/collaboratorProfileApi';
import {
  BRAZILIAN_STATE_OPTIONS,
  EDUCATION_DEGREE_OPTIONS,
  EDUCATION_STATUS_OPTIONS,
  EDUCATION_TYPE_OPTIONS,
  EMPLOYMENT_TYPE_OPTIONS,
  GENDER_OPTIONS,
  JOB_LEVEL_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  RACE_ETHNICITY_OPTIONS,
  SEXUAL_ORIENTATION_OPTIONS,
  DISABILITY_TYPE_OPTIONS,
} from '../../services/collaboratorProfileOptions';
import { isValidCnpj, normalizeCnpj } from '../../utils/cnpjValidator';
import { JobPosition, listJobPositions } from '../../services/jobPositionsApi';
import {
  JobPositionAttribution,
  listJobPositionAttributions,
} from '../../services/jobPositionAttributionsApi';

type DependentFormRow = CollaboratorDependent & { key: string };
type EducationFormRow = CollaboratorEducation & { key: string };
type CollaboratorProfileTabId = 'registration' | 'attributions' | 'indicators';

const PROFILE_TABS: SettingsTabItem<CollaboratorProfileTabId>[] = [
  { id: 'registration', label: 'Cadastro' },
  { id: 'attributions', label: 'Atribuições' },
  { id: 'indicators', label: 'Indicadores de desempenho' },
];

function createDependentRow(): DependentFormRow {
  return {
    key: crypto.randomUUID(),
    name: '',
    birthDate: null,
    relationship: '',
    cpf: '',
  };
}

function createEducationRow(): EducationFormRow {
  return {
    key: crypto.randomUUID(),
    course: '',
    educationType: 'Graduacao',
    educationDegree: 'Superior',
    institution: '',
    educationStatus: 'EmAndamento',
  };
}

function mapProfileToForm(profile: CollaboratorProfile): {
  fields: Omit<CollaboratorProfile, 'dependents' | 'educations'>;
  dependents: DependentFormRow[];
  educations: EducationFormRow[];
} {
  return {
    fields: profile,
    dependents: profile.dependents.map((item) => ({
      ...item,
      key: item.id ?? crypto.randomUUID(),
      birthDate: item.birthDate ?? null,
      relationship: item.relationship ?? '',
      cpf: item.cpf ?? '',
    })),
    educations: profile.educations.map((item) => ({
      ...item,
      key: item.id ?? crypto.randomUUID(),
      educationDegree: item.educationDegree ?? '',
      institution: item.institution ?? '',
    })),
  };
}

export function CollaboratorProfilePage() {
  const { userId = '' } = useParams();
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('access-control.users');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [jobPositions, setJobPositions] = useState<JobPosition[]>([]);
  const [jobAttributions, setJobAttributions] = useState<JobPositionAttribution[]>([]);
  const [loadingAttributions, setLoadingAttributions] = useState(false);
  const [userName, setUserName] = useState('');
  const [form, setForm] = useState({
    cpf: '',
    birthDate: '',
    raceEthnicity: '',
    gender: '',
    sexualOrientation: '',
    isPersonWithDisability: false,
    disabilityTypes: [] as string[],
    employmentType: '',
    jobPositionId: '',
    jobLevel: '',
    address: '',
    postalCode: '',
    city: '',
    state: '',
    maritalStatus: '',
    phone: '',
    personalEmail: '',
    cnpj: '',
  });
  const [dependents, setDependents] = useState<DependentFormRow[]>([]);
  const [educations, setEducations] = useState<EducationFormRow[]>([]);
  const [activeTab, setActiveTab] = useState<CollaboratorProfileTabId>('registration');

  const requiresCnpj = form.employmentType === 'PrestadorServico';

  const jobPositionOptions = useMemo(
    () => jobPositions.filter((position) => position.isActive),
    [jobPositions],
  );

  useEffect(() => {
    if (!form.jobPositionId || !form.jobLevel) {
      setJobAttributions([]);
      return;
    }

    void (async () => {
      setLoadingAttributions(true);
      try {
        setJobAttributions(
          await listJobPositionAttributions(form.jobPositionId, form.jobLevel),
        );
      } catch {
        setJobAttributions([]);
      } finally {
        setLoadingAttributions(false);
      }
    })();
  }, [form.jobPositionId, form.jobLevel]);

  async function loadData(): Promise<void> {
    setLoading(true);
    try {
      const [profile, positionsResult] = await Promise.all([
        getCollaboratorProfile(userId),
        listJobPositions({ activeOnly: true, page: 1, pageSize: 200 }),
      ]);

      const mapped = mapProfileToForm(profile);
      setUserName(profile.userName);
      setJobPositions(positionsResult.items);
      setForm({
        cpf: profile.cpf ?? '',
        birthDate: profile.birthDate ?? '',
        raceEthnicity: profile.raceEthnicity ?? '',
        gender: profile.gender ?? '',
        sexualOrientation: profile.sexualOrientation ?? '',
        isPersonWithDisability: profile.isPersonWithDisability ?? false,
        disabilityTypes: [...(profile.disabilityTypes ?? [])],
        employmentType: profile.employmentType ?? '',
        jobPositionId: profile.jobPositionId ?? '',
        jobLevel: profile.jobLevel ?? '',
        address: profile.address ?? '',
        postalCode: profile.postalCode ?? '',
        city: profile.city ?? '',
        state: profile.state ?? '',
        maritalStatus: profile.maritalStatus ?? '',
        phone: profile.phone ?? '',
        personalEmail: profile.personalEmail ?? '',
        cnpj: profile.cnpj ?? '',
      });
      setDependents(mapped.dependents);
      setEducations(mapped.educations);
    } catch {
      toast.error('Falha ao carregar cadastro do colaborador.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    if (requiresCnpj && !form.cnpj.trim()) {
      toast.error('CNPJ é obrigatório para prestadores de serviço.');
      return;
    }

    if (form.cnpj.trim() && !isValidCnpj(form.cnpj)) {
      toast.error('CNPJ inválido. Aceita formato numérico ou alfanumérico (14 caracteres).');
      return;
    }

    if (form.isPersonWithDisability && form.disabilityTypes.length === 0) {
      toast.error('Selecione ao menos um tipo de deficiência.');
      return;
    }

    setSaving(true);
    try {
      await upsertCollaboratorProfile(userId, {
        cpf: form.cpf || undefined,
        birthDate: form.birthDate || null,
        raceEthnicity: form.raceEthnicity || undefined,
        gender: form.gender || undefined,
        sexualOrientation: form.sexualOrientation || undefined,
        isPersonWithDisability: form.isPersonWithDisability,
        disabilityTypes: form.isPersonWithDisability ? form.disabilityTypes : [],
        employmentType: form.employmentType || undefined,
        jobPositionId: form.jobPositionId || null,
        jobLevel: form.jobLevel || undefined,
        address: form.address || undefined,
        postalCode: form.postalCode || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        maritalStatus: form.maritalStatus || undefined,
        phone: form.phone || undefined,
        personalEmail: form.personalEmail || undefined,
        cnpj: form.cnpj.trim() ? normalizeCnpj(form.cnpj) : undefined,
        dependents: dependents.map((item) => ({
          name: item.name,
          birthDate: item.birthDate || null,
          relationship: item.relationship || undefined,
          cpf: item.cpf || undefined,
        })),
        educations: educations.map((item) => ({
          course: item.course,
          educationType: item.educationType,
          educationDegree: item.educationDegree || undefined,
          institution: item.institution || undefined,
          educationStatus: item.educationStatus,
        })),
      });
      toast.success('Cadastro do colaborador salvo.');
      await loadData();
    } catch {
      toast.error('Falha ao salvar cadastro do colaborador.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="erp-loading">Carregando...</p>;
  }

  return (
    <PageShell>
      <div>
        <Link to="/admin/users" className="erp-link-back">
          ← Voltar para colaboradores
        </Link>
        <PageHeader
          title={`Cadastro — ${userName}`}
          description="Informações pessoais, profissionais, atribuições do cargo e indicadores de desempenho."
        />
      </div>

      <SettingsTabBar tabs={PROFILE_TABS} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'attributions' ? (
        <section className="erp-card">
          <div className="erp-card-body space-y-3">
            <h2 className="font-semibold text-secondary-900">Atribuições do cargo</h2>
            <p className="text-sm text-secondary-500">
              Atribuições do cargo e nível atuais. Itens de orientação — não são avaliáveis.
            </p>
            {!form.jobPositionId || !form.jobLevel ? (
              <p className="text-sm text-secondary-500">
                Selecione cargo e nível na aba Cadastro para visualizar as atribuições.
              </p>
            ) : loadingAttributions ? (
              <p className="text-sm text-secondary-500">Carregando atribuições...</p>
            ) : jobAttributions.length === 0 ? (
              <p className="text-sm text-secondary-500">
                Nenhuma atribuição cadastrada para este cargo e nível.
              </p>
            ) : (
              <ul className="space-y-2">
                {jobAttributions.map((item) => (
                  <JobAttributionListItem
                    key={item.id}
                    kindName={item.kindName}
                    title={item.title}
                    description={item.description}
                  />
                ))}
              </ul>
            )}
          </div>
        </section>
      ) : null}

      {activeTab === 'indicators' ? (
        <section className="erp-card">
          <div className="erp-card-body space-y-3">
            <h2 className="font-semibold text-secondary-900">Indicadores de desempenho</h2>
            <p className="text-sm text-secondary-500">
              Indicadores em que o colaborador é avaliado, conforme o nível do cargo.
            </p>
            <p className="rounded-lg border border-dashed border-secondary-200 bg-secondary-50 px-4 py-6 text-center text-sm text-secondary-500">
              Em breve — o cadastro de indicadores de desempenho por nível ainda não está disponível.
            </p>
          </div>
        </section>
      ) : null}

      {activeTab === 'registration' ? (
      <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
        <section className="erp-card">
          <div className="erp-card-body space-y-3">
            <h2 className="font-semibold text-secondary-900">Dados pessoais</h2>
            <div className="erp-form-grid md:grid-cols-3">
              <input
                className="erp-input-compact"
                placeholder="CPF"
                value={form.cpf}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, cpf: event.target.value })}
              />
              <input
                className="erp-input-compact"
                type="date"
                placeholder="Data de nascimento"
                value={form.birthDate}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, birthDate: event.target.value })}
              />
              <select
                className="erp-input-compact"
                value={form.raceEthnicity}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, raceEthnicity: event.target.value })}
              >
                <option value="">Raça/etnia</option>
                {RACE_ETHNICITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <select
                className="erp-input-compact"
                value={form.gender}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, gender: event.target.value })}
              >
                <option value="">Gênero</option>
                {GENDER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <select
                className="erp-input-compact"
                value={form.sexualOrientation}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, sexualOrientation: event.target.value })}
              >
                <option value="">Orientação sexual</option>
                {SEXUAL_ORIENTATION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <select
                className="erp-input-compact"
                value={form.maritalStatus}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, maritalStatus: event.target.value })}
              >
                <option value="">Estado civil</option>
                {MARITAL_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <input
                className="erp-input-compact"
                placeholder="Telefone"
                value={form.phone}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, phone: event.target.value })}
              />
              <input
                className="erp-input-compact md:col-span-3"
                placeholder="E-mail pessoal"
                type="email"
                value={form.personalEmail}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, personalEmail: event.target.value })}
              />
            </div>

            <div className="space-y-2 border-t border-secondary-200 pt-3">
              <label className="erp-label-inline">
                <input
                  type="checkbox"
                  checked={form.isPersonWithDisability}
                  disabled={!canWrite}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setForm({
                      ...form,
                      isPersonWithDisability: checked,
                      disabilityTypes: checked ? form.disabilityTypes : [],
                    });
                  }}
                />
                Pessoa com deficiência (PCD)
              </label>

              {form.isPersonWithDisability ? (
                <div className="erp-form-grid md:grid-cols-3">
                  {DISABILITY_TYPE_OPTIONS.map((option) => (
                    <label key={option.value} className="erp-label-inline">
                      <input
                        type="checkbox"
                        disabled={!canWrite}
                        checked={form.disabilityTypes.includes(option.value)}
                        onChange={(event) => {
                          setForm((current) => ({
                            ...current,
                            disabilityTypes: event.target.checked
                              ? [...current.disabilityTypes, option.value]
                              : current.disabilityTypes.filter((item) => item !== option.value),
                          }));
                        }}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section className="erp-card">
          <div className="erp-card-body space-y-3">
            <h2 className="font-semibold text-secondary-900">Dados profissionais</h2>
            <div className="erp-form-grid md:grid-cols-3">
              <select
                className="erp-input-compact"
                value={form.employmentType}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, employmentType: event.target.value })}
              >
                <option value="">Tipo de colaborador</option>
                {EMPLOYMENT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <select
                className="erp-input-compact"
                value={form.jobPositionId}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, jobPositionId: event.target.value })}
              >
                <option value="">Cargo</option>
                {jobPositionOptions.map((position) => (
                  <option key={position.id} value={position.id}>{position.name}</option>
                ))}
              </select>
              <select
                className="erp-input-compact"
                value={form.jobLevel}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, jobLevel: event.target.value })}
              >
                <option value="">Nível do cargo</option>
                {JOB_LEVEL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {requiresCnpj ? (
                <input
                  className="erp-input-compact md:col-span-3"
                  placeholder="CNPJ numérico ou alfanumérico (obrigatório para prestador)"
                  value={form.cnpj}
                  disabled={!canWrite}
                  required
                  onChange={(event) => setForm({ ...form, cnpj: event.target.value })}
                />
              ) : (
                <input
                  className="erp-input-compact md:col-span-3"
                  placeholder="CNPJ numérico ou alfanumérico (opcional)"
                  value={form.cnpj}
                  disabled={!canWrite}
                  onChange={(event) => setForm({ ...form, cnpj: event.target.value })}
                />
              )}
            </div>
          </div>
        </section>

        <section className="erp-card">
          <div className="erp-card-body space-y-3">
            <h2 className="font-semibold text-secondary-900">Endereço</h2>
            <div className="erp-form-grid md:grid-cols-4">
              <input
                className="erp-input-compact md:col-span-2"
                placeholder="Endereço"
                value={form.address}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, address: event.target.value })}
              />
              <input
                className="erp-input-compact"
                placeholder="CEP"
                value={form.postalCode}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, postalCode: event.target.value })}
              />
              <input
                className="erp-input-compact"
                placeholder="Cidade"
                value={form.city}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, city: event.target.value })}
              />
              <select
                className="erp-input-compact"
                value={form.state}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, state: event.target.value })}
              >
                <option value="">UF</option>
                {BRAZILIAN_STATE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className="erp-card">
          <div className="erp-card-body space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-semibold text-secondary-900">Dependentes</h2>
              {canWrite ? (
                <button
                  type="button"
                  className="erp-btn-secondary"
                  onClick={() => setDependents((current) => [...current, createDependentRow()])}
                >
                  Adicionar dependente
                </button>
              ) : null}
            </div>
            {dependents.length === 0 ? (
              <p className="text-sm text-secondary-600">Nenhum dependente cadastrado.</p>
            ) : (
              <div className="space-y-3">
                {dependents.map((dependent, index) => (
                  <div key={dependent.key} className="erp-form-grid md:grid-cols-5 items-end gap-2 border border-secondary-200 rounded-lg p-3">
                    <input
                      className="erp-input-compact"
                      placeholder="Nome"
                      value={dependent.name}
                      disabled={!canWrite}
                      required
                      onChange={(event) => {
                        const value = event.target.value;
                        setDependents((current) => current.map((item, itemIndex) => (
                          itemIndex === index ? { ...item, name: value } : item
                        )));
                      }}
                    />
                    <input
                      className="erp-input-compact"
                      type="date"
                      value={dependent.birthDate ?? ''}
                      disabled={!canWrite}
                      onChange={(event) => {
                        const value = event.target.value || null;
                        setDependents((current) => current.map((item, itemIndex) => (
                          itemIndex === index ? { ...item, birthDate: value } : item
                        )));
                      }}
                    />
                    <input
                      className="erp-input-compact"
                      placeholder="Parentesco"
                      value={dependent.relationship}
                      disabled={!canWrite}
                      onChange={(event) => {
                        const value = event.target.value;
                        setDependents((current) => current.map((item, itemIndex) => (
                          itemIndex === index ? { ...item, relationship: value } : item
                        )));
                      }}
                    />
                    <input
                      className="erp-input-compact"
                      placeholder="CPF"
                      value={dependent.cpf}
                      disabled={!canWrite}
                      onChange={(event) => {
                        const value = event.target.value;
                        setDependents((current) => current.map((item, itemIndex) => (
                          itemIndex === index ? { ...item, cpf: value } : item
                        )));
                      }}
                    />
                    {canWrite ? (
                      <DeleteIconButton
                        label="Remover dependente"
                        onClick={() => setDependents((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="erp-card">
          <div className="erp-card-body space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-semibold text-secondary-900">Formação</h2>
              {canWrite ? (
                <button
                  type="button"
                  className="erp-btn-secondary"
                  onClick={() => setEducations((current) => [...current, createEducationRow()])}
                >
                  Adicionar formação
                </button>
              ) : null}
            </div>
            {educations.length === 0 ? (
              <p className="text-sm text-secondary-600">Nenhuma formação cadastrada.</p>
            ) : (
              <div className="space-y-3">
                {educations.map((education, index) => (
                  <div key={education.key} className="border border-secondary-200 rounded-lg p-3 space-y-2">
                    <div className="erp-form-grid md:grid-cols-3">
                      <input
                        className="erp-input-compact md:col-span-2"
                        placeholder="Curso"
                        value={education.course}
                        disabled={!canWrite}
                        required
                        onChange={(event) => {
                          const value = event.target.value;
                          setEducations((current) => current.map((item, itemIndex) => (
                            itemIndex === index ? { ...item, course: value } : item
                          )));
                        }}
                      />
                      <select
                        className="erp-input-compact"
                        value={education.educationType}
                        disabled={!canWrite}
                        onChange={(event) => {
                          const value = event.target.value;
                          setEducations((current) => current.map((item, itemIndex) => (
                            itemIndex === index ? { ...item, educationType: value } : item
                          )));
                        }}
                      >
                        {EDUCATION_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <select
                        className="erp-input-compact"
                        value={education.educationDegree ?? ''}
                        disabled={!canWrite}
                        onChange={(event) => {
                          const value = event.target.value;
                          setEducations((current) => current.map((item, itemIndex) => (
                            itemIndex === index ? { ...item, educationDegree: value } : item
                          )));
                        }}
                      >
                        <option value="">Grau</option>
                        {EDUCATION_DEGREE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <input
                        className="erp-input-compact md:col-span-2"
                        placeholder="Instituição"
                        value={education.institution}
                        disabled={!canWrite}
                        onChange={(event) => {
                          const value = event.target.value;
                          setEducations((current) => current.map((item, itemIndex) => (
                            itemIndex === index ? { ...item, institution: value } : item
                          )));
                        }}
                      />
                      <div className="flex items-end gap-2">
                        <select
                          className="erp-input-compact flex-1"
                          value={education.educationStatus}
                          disabled={!canWrite}
                          onChange={(event) => {
                            const value = event.target.value;
                            setEducations((current) => current.map((item, itemIndex) => (
                              itemIndex === index ? { ...item, educationStatus: value } : item
                            )));
                          }}
                        >
                          {EDUCATION_STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        {canWrite ? (
                          <DeleteIconButton
                            label="Remover formação"
                            onClick={() => setEducations((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {canWrite ? (
          <div className="erp-btn-actions">
            <button type="submit" className="erp-btn-primary" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar cadastro'}
            </button>
          </div>
        ) : null}
      </form>
      ) : null}
    </PageShell>
  );
}
